import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/cn";
import { buttonClasses, type ButtonSize, type ButtonVariant } from "@/components/ui/Button";

export interface LinkButtonProps {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A link that looks like a button.
 *
 * Navigation stays an anchor so it can be opened in a new tab, middle-clicked
 * and read correctly by a screen reader. It borrows `buttonClasses` outright,
 * so a link and a button of the same variant are the same object on screen —
 * including the variants Button gains later.
 */
export function LinkButton({
  to,
  variant = "secondary",
  size = "md",
  icon,
  fullWidth = false,
  className,
  children,
}: LinkButtonProps) {
  return (
    <Link to={to} className={cn(buttonClasses(variant, size, fullWidth), className)}>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      <span>{children}</span>
    </Link>
  );
}
